import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
  e.preventDefault();
  if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
    toast.error("Please fill in your name, email and message.");
    return;
  }
  setSubmitting(true);
  try {
    await api.post("/contact.php", {
      name: form.name,
      email: form.email,
      subject: form.subject || "General",
      message: form.message,
    }, {
      headers: { "Content-Type": "application/json" }
    });

    toast.success("Message sent! We'll be in touch over chai soon. ☕");
    setForm(EMPTY);
  } catch (err) {
    console.error("Submission error:", err);
    toast.error("Something went wrong. Please try again.");
  } finally {
    setSubmitting(false);
  }
};


  const field =
    "w-full bg-transparent border-b-2 border-chai-ink/30 focus:border-chai-red outline-none py-4 font-body text-lg placeholder:text-muted-foreground transition-colors";

  return (
    <div data-testid="contact-page">
      <section className="w-full max-w-none px-4 md:px-8 lg:px-12 pt-40 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-chai-red mb-6">
                Get In Touch
              </p>
              <h1 className="font-heading font-bold uppercase tracking-tighter leading-[0.85] text-[13vw] md:text-[6vw]">
                Let&apos;s have<br />
                <span className="text-chai-red">a chai.</span>
              </h1>
              <p className="font-body text-lg text-muted-foreground mt-8 max-w-md">
                Questions, collaborations, or just want to talk learning? Drop us a line — we reply to every message.
              </p>
              <div className="mt-10 space-y-4">
                <a
                  href="mailto:ask.chaied@gmail.com"
                  className="flex items-center gap-3 font-body text-lg link-underline w-fit"
                  data-testid="contact-email"
                >
                  <Mail size={18} className="text-chai-red" /> ask.chaied@gmail.com
                </a>
                <a
                  href="tel:+917982470821"
                  className="flex items-center gap-3 font-body text-lg link-underline w-fit"
                  data-testid="contact-phone"
                >
                  <Phone size={18} className="text-chai-red" /> +91 79824 70821
                </a>
                <p className="flex items-center gap-3 font-body text-lg">
                  <MapPin size={18} className="text-chai-red" /> India — everywhere chai is served
                </p>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <input
                  data-testid="contact-name"
                  className={field}
                  placeholder="Your name"
                  value={form.name}
                  onChange={update("name")}
                />
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className={field}
                  placeholder="Your email"
                  value={form.email}
                  onChange={update("email")}
                />
                <input
                  data-testid="contact-subject"
                  className={field}
                  placeholder="Subject (optional)"
                  value={form.subject}
                  onChange={update("subject")}
                />
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  className={`${field} resize-none`}
                  placeholder="Your message"
                  value={form.message}
                  onChange={update("message")}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 bg-chai-ink text-white px-10 py-5 font-mono text-xs uppercase tracking-widest hover:bg-chai-red transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
